
import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import CTASection from "../components/CTASection";

import {
  ArrowRight,
  ArrowUpRight,
  BadgeCheck,
  BedDouble,
  Building2,
  CalendarCheck2,
  Check,
  CheckCircle2,
  ChevronRight,
  Compass,
  Globe2,
  Heart,
  Hotel,
  MapPin,
  Search,
  ShieldCheck,
  Sparkles,
  Star,
  Users,
  Wallet,
} from "lucide-react";

/* ==========================================================
   IMAGES
========================================================== */

const images = {
  hero:
    "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?auto=format&fit=crop&w=1600&q=85",

  hotel:
    "https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=1200&q=85",

  room:
    "https://images.unsplash.com/photo-1611892440504-42a792e24d32?auto=format&fit=crop&w=1200&q=85",

  pool:
    "https://images.unsplash.com/photo-1571896349842-33c89424de2d?auto=format&fit=crop&w=1400&q=85",

  dubai:
    "https://images.unsplash.com/photo-1546412414-e1885259563a?auto=format&fit=crop&w=1600&q=85",
};

/* ==========================================================
   BENEFITS
========================================================== */

const benefits = [
  {
    icon: Search,
    title: "Easy Hotel Discovery",
    description:
      "Explore Dubai accommodation in one convenient place and discover options that suit your travel plans.",
  },
  {
    icon: Wallet,
    title: "Options for Your Budget",
    description:
      "Browse a range of hotel choices and compare available rates before selecting your preferred stay.",
  },
  {
    icon: MapPin,
    title: "Popular Dubai Locations",
    description:
      "Discover accommodation in different Dubai neighbourhoods, from lively city areas to relaxing destinations.",
  },
  {
    icon: BedDouble,
    title: "Comfortable Stays",
    description:
      "Explore room details, accommodation features, and hotel information to help you make your choice.",
  },
  {
    icon: CheckCircle2,
    title: "Informed Decisions",
    description:
      "Review hotel information and available booking details before proceeding with your reservation.",
  },
  {
    icon: Heart,
    title: "Travel Your Way",
    description:
      "Whether you are visiting for business, family time, or a holiday, find accommodation that fits your trip.",
  },
];

/* ==========================================================
   HOW IT WORKS
========================================================== */

const steps = [
  {
    number: "01",
    icon: MapPin,
    title: "Choose Your Destination",
    description:
      "Start with your preferred Dubai location, travel dates, and accommodation preferences.",
  },
  {
    number: "02",
    icon: Search,
    title: "Explore Available Hotels",
    description:
      "Browse hotel options, review room information, and compare the available rates.",
  },
  {
    number: "03",
    icon: CalendarCheck2,
    title: "Plan Your Stay",
    description:
      "Choose the hotel and room that fit your needs, then continue with the booking process.",
  },
];

/* ==========================================================
   SECTION HEADING
========================================================== */

function SectionHeading({
  label,
  title,
  description,
  center = false,
}) {
  return (
    <div className={center ? "mx-auto max-w-[720px] text-center" : ""}>
      <div
        className={`mb-4 flex items-center gap-3 ${center ? "justify-center" : ""
          }`}
      >
        <span className="h-[2px] w-8 bg-[#168B43]" />

        <span className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#168B43]">
          {label}
        </span>

        {center && <span className="h-[2px] w-8 bg-[#168B43]" />}
      </div>

      <h2 className="text-[34px] font-extrabold leading-[1.18] tracking-[-0.04em] text-[#17324D] sm:text-[44px] lg:text-[49px]">
        {title}
      </h2>

      {description && (
        <p
          className={`mt-5 text-[14px] leading-8 text-[#718096] ${center ? "mx-auto max-w-[590px]" : "max-w-[570px]"
            }`}
        >
          {description}
        </p>
      )}
    </div>
  );
}

/* ==========================================================
   MAIN ABOUT PAGE
========================================================== */

export default function AboutUs() {
  return (
    <>
      <Helmet>
        <title>About Us | Dubai Budgetive Hotels</title>

        <meta
          name="description"
          content="Learn more about Dubai Budgetive Hotels. Discover Dubai accommodation, explore hotel options, compare available rates and find a stay that suits your travel plans."
        />

        <meta
          property="og:title"
          content="About Us | Dubai Budgetive Hotels"
        />

        <meta
          property="og:description"
          content="Discover a convenient way to explore hotels and plan your next stay in Dubai."
        />

        <meta property="og:type" content="website" />
      </Helmet>

      <main className="overflow-hidden bg-white text-[#17324D]">

        {/* =====================================================
            HERO - COMPLETELY NEW SPLIT DESIGN
        ===================================================== */}

        <section className="relative overflow-hidden bg-[#F5FAF7] pt-28 sm:pt-32 lg:pt-36">
          <div className="absolute right-0 top-0 h-[450px] w-[450px] rounded-full bg-[#E1F3E8]/70 blur-[100px]" />

          <div className="relative mx-auto grid w-full max-w-[1536px] items-center gap-12 px-4 pb-20 sm:px-6 lg:grid-cols-[0.95fr_1.05fr] lg:gap-16 lg:px-8 lg:pb-28">

            {/* LEFT TEXT */}
            <div className="relative z-10">
              <div className="mb-8 flex flex-wrap items-center gap-2 text-xs text-[#718096]">
                <Link to="/" className="hover:text-[#168B43]">
                  Home
                </Link>

                <ChevronRight size={13} />

                <span className="font-bold text-[#168B43]">
                  About Us
                </span>
              </div>

              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-[#CFE9D8] bg-[#E8F7EE] px-4 py-2 text-[11px] font-extrabold uppercase tracking-[0.15em] text-[#168B43]">
                <Sparkles size={15} />
                Welcome to Dubai Budgetive Hotels
              </div>

              <h1 className="max-w-[720px] text-[43px] font-extrabold leading-[1.13] tracking-[-0.05em] text-[#17324D] sm:text-[58px] lg:text-[65px]">
                Discover Dubai.
                <br />
                Find Your{" "}
                <span className="text-[#168B43]">
                  Perfect Stay.
                </span>
              </h1>

              <p className="mt-7 max-w-[570px] text-[15px] leading-8 text-[#718096]">
                We believe finding the right hotel should be one
                of the easiest parts of planning your trip.
                Explore Dubai accommodation, discover different
                locations, and choose a stay that suits your
                plans and budget.
              </p>

              <div className="mt-9 flex flex-wrap items-center gap-4">
                <Link
                  to="/hotels"
                  className="inline-flex items-center gap-3 rounded-xl bg-[#168B43] px-7 py-4 text-sm font-extrabold text-white shadow-[0_10px_25px_rgba(22,139,67,0.17)] transition hover:-translate-y-0.5 hover:bg-[#087D37]"
                >
                  Explore Hotels
                  <ArrowRight size={17} />
                </Link>

                <a
                  href="#our-story"
                  className="inline-flex items-center gap-2 rounded-xl border border-[#D6E6DC] bg-white px-6 py-4 text-sm font-extrabold text-[#17324D] transition hover:border-[#168B43] hover:text-[#168B43]"
                >
                  Our Story
                  <ArrowUpRight size={17} />
                </a>
              </div>

              <div className="mt-12 flex flex-wrap gap-6 border-t border-[#DCEAE1] pt-7">
                {[
                  { icon: Hotel, label: "Hotel Discovery" },
                  { icon: MapPin, label: "Dubai Destinations" },
                  { icon: Heart, label: "Travel Your Way" },
                ].map(({ icon: Icon, label }) => (
                  <div
                    key={label}
                    className="flex items-center gap-2 text-xs font-bold text-[#64748B]"
                  >
                    <CheckCircle2
                      size={17}
                      className="text-[#168B43]"
                    />
                    {label}
                  </div>
                ))}
              </div>
            </div>

            {/* RIGHT IMAGE COLLAGE */}
            <div className="relative min-h-[420px] sm:min-h-[560px]">
              <div className="absolute left-[7%] top-[4%] h-[83%] w-[78%] overflow-hidden rounded-[25px] shadow-[0_25px_65px_rgba(6,78,59,0.16)]">
                <img
                  src={images.hero}
                  alt="Dubai skyline and hotels"
                  className="h-full w-full object-cover"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-[#064E3B]/35 to-transparent" />
              </div>

              <div className="absolute bottom-[1%] right-0 h-[48%] w-[46%] overflow-hidden rounded-[22px] border-[6px] border-[#F5FAF7] bg-white shadow-[0_15px_45px_rgba(6,78,59,0.15)]">
                <img
                  src={images.hotel}
                  alt="Dubai hotel and resort"
                  className="h-full w-full object-cover"
                />
              </div>

              <div className="absolute right-[1%] top-[2%] flex items-center gap-3 rounded-2xl border border-[#E6EFE9] bg-white p-4 shadow-[0_15px_40px_rgba(6,78,59,0.13)] sm:right-[2%] sm:p-5">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#E8F7EE] text-[#168B43]">
                  <MapPin size={22} />
                </div>

                <div>
                  <p className="text-sm font-extrabold text-[#17324D]">
                    Explore Dubai
                  </p>
                  <p className="mt-1 text-[11px] text-[#8492A6]">
                    Hotels & Destinations
                  </p>
                </div>
              </div>

              <div className="absolute bottom-[12%] left-0 flex items-center gap-3 rounded-2xl border border-[#E6EFE9] bg-white p-4 shadow-[0_15px_40px_rgba(6,78,59,0.13)] sm:p-5">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#E8F7EE] text-[#168B43]">
                  <BedDouble size={22} />
                </div>

                <div>
                  <p className="text-sm font-extrabold text-[#17324D]">
                    Find Your Stay
                  </p>
                  <p className="mt-1 text-[11px] text-[#8492A6]">
                    Explore Hotel Options
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* =====================================================
            OUR STORY
        ===================================================== */}

        <section
          id="our-story"
          className="scroll-mt-24 bg-white py-20 sm:py-28"
        >
          <div className="mx-auto grid w-full max-w-[1536px] items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:gap-20 lg:px-8">

            {/* IMAGE */}
            <div className="relative">
              <div className="overflow-hidden rounded-[24px]">
                <img
                  src={images.room}
                  alt="Comfortable hotel accommodation"
                  loading="lazy"
                  className="h-[350px] w-full object-cover sm:h-[510px]"
                />
              </div>

              <div className="absolute -bottom-6 right-5 flex max-w-[270px] items-center gap-4 rounded-2xl bg-[#064E3B] p-5 text-white shadow-[0_20px_45px_rgba(6,78,59,0.22)] sm:right-[-20px] sm:p-6">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/15 text-[#B6F2CB]">
                  <Heart size={24} />
                </div>

                <div>
                  <p className="text-sm font-extrabold text-white">
                    Your Trip, Your Choice
                  </p>

                  <p className="mt-1 text-xs leading-6 text-white/70">
                    Discover accommodation that works for you.
                  </p>
                </div>
              </div>
            </div>

            {/* CONTENT */}
            <div className="pt-8 lg:pt-0">
              <SectionHeading
                label="Our Story"
                title={
                  <>
                    Making Hotel Discovery{" "}
                    <span className="text-[#168B43]">
                      Simple.
                    </span>
                  </>
                }
                description="We want to make finding accommodation in Dubai a convenient and enjoyable part of planning your journey."
              />

              <p className="mt-5 text-[14px] leading-8 text-[#718096]">
                Planning a trip involves many decisions, from
                choosing where to go to deciding where to stay.
                Dubai Budgetive Hotels helps visitors explore
                accommodation options across Dubai and compare
                available hotel rates.
              </p>

              <p className="mt-4 text-[14px] leading-8 text-[#718096]">
                Whether you're planning a family holiday, a
                business visit, or a relaxing getaway, our goal
                is to help you discover hotel options that fit
                your travel style.
              </p>

              <div className="mt-7 grid gap-4 sm:grid-cols-2">
                {[
                  "Explore Dubai Hotels",
                  "Compare Available Rates",
                  "Discover Popular Areas",
                  "Choose Your Preferred Stay",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#E8F7EE] text-[#168B43]">
                      <Check size={15} strokeWidth={3} />
                    </span>

                    <span className="text-[13px] font-bold text-[#425466]">
                      {item}
                    </span>
                  </div>
                ))}
              </div>

              <Link
                to="/hotels"
                className="mt-9 inline-flex items-center gap-3 rounded-xl bg-[#168B43] px-7 py-4 text-sm font-extrabold text-white transition hover:bg-[#087D37]"
              >
                Discover Available Hotels
                <ArrowRight size={17} />
              </Link>
            </div>
          </div>
        </section>

        {/* =====================================================
            MISSION & VISION
        ===================================================== */}

        <section className="bg-[#F5FAF7] py-20 sm:py-24">
          <div className="mx-auto w-full max-w-[1536px] px-4 sm:px-6 lg:px-8">
            <SectionHeading
              label="What Drives Us"
              title={
                <>
                  Built Around{" "}
                  <span className="text-[#168B43]">
                    Your Journey.
                  </span>
                </>
              }
              description="Our focus is on helping visitors discover accommodation options and make informed decisions when planning their Dubai stay."
              center
            />

            <div className="mt-12 grid gap-6 lg:grid-cols-2">

              {/* MISSION */}
              <article className="group relative overflow-hidden rounded-[22px] bg-[#064E3B] p-8 text-white sm:p-10 lg:p-12">
                <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full border border-white/10" />
                <div className="absolute -right-6 -top-6 h-44 w-44 rounded-full border border-white/10" />

                <div className="relative">
                  <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-white/15 text-[#B6F2CB]">
                    <Compass size={27} />
                  </div>

                  <p className="mt-10 text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#B6F2CB]">
                    What We Do
                  </p>

                  <h3 className="mt-3 text-[32px] font-extrabold tracking-tight text-white sm:text-[39px]">
                    Our Mission
                  </h3>

                  <p className="mt-5 max-w-[520px] text-[14px] leading-8 text-white/75">
                    To make hotel discovery easier by helping
                    travellers explore Dubai accommodation,
                    compare available rates, and find options
                    suited to their travel plans.
                  </p>

                  <div className="mt-10 flex items-center gap-2 text-sm font-bold text-[#B6F2CB]">
                    Explore with confidence
                    <ArrowUpRight size={18} />
                  </div>
                </div>
              </article>

              {/* VISION */}
              <article className="group relative overflow-hidden rounded-[22px] border border-[#DCEAE1] bg-white p-8 sm:p-10 lg:p-12">
                <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-[#E8F7EE]" />

                <div className="relative">
                  <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-[#E8F7EE] text-[#168B43]">
                    <Globe2 size={27} />
                  </div>

                  <p className="mt-10 text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#168B43]">
                    Where We're Going
                  </p>

                  <h3 className="mt-3 text-[32px] font-extrabold tracking-tight text-[#17324D] sm:text-[39px]">
                    Our Vision
                  </h3>

                  <p className="mt-5 max-w-[520px] text-[14px] leading-8 text-[#718096]">
                    To be a helpful starting point for visitors
                    planning a Dubai trip, making it easier
                    to discover hotels, explore neighbourhoods,
                    and find a place to stay.
                  </p>

                  <div className="mt-10 flex items-center gap-2 text-sm font-bold text-[#168B43]">
                    Discover more possibilities
                    <ArrowUpRight size={18} />
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>

        {/* =====================================================
            WHY CHOOSE US
        ===================================================== */}

        <section className="bg-white py-20 sm:py-24">
          <div className="mx-auto w-full max-w-[1536px] px-4 sm:px-6 lg:px-8">
            <SectionHeading
              label="Why Choose Us"
              title={
                <>
                  A Better Way to{" "}
                  <span className="text-[#168B43]">
                    Find Your Stay.
                  </span>
                </>
              }
              description="Discover features that make exploring hotels and planning your Dubai accommodation more convenient."
              center
            />

            <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {benefits.map(({ icon: Icon, title, description }) => (
                <article
                  key={title}
                  className="group rounded-[20px] border border-[#E3ECE6] bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-[#B8DFC6] hover:shadow-[0_18px_45px_rgba(6,78,59,0.09)] sm:p-8"
                >
                  <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-[#E8F7EE] text-[#168B43] transition group-hover:bg-[#168B43] group-hover:text-white">
                    <Icon size={25} strokeWidth={1.8} />
                  </div>

                  <h3 className="mt-6 text-[19px] font-extrabold text-[#17324D]">
                    {title}
                  </h3>

                  <p className="mt-3 text-[13px] leading-7 text-[#718096]">
                    {description}
                  </p>

                  <div className="mt-6 h-[3px] w-10 rounded-full bg-[#168B43]" />
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* =====================================================
            FEATURED DUBAI SECTION
        ===================================================== */}

        <section className="bg-[#F5FAF7] py-20 sm:py-24">
          <div className="mx-auto grid w-full max-w-[1536px] items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:gap-20 lg:px-8">
            <div>
              <SectionHeading
                label="Discover Dubai"
                title={
                  <>
                    A City Full of{" "}
                    <span className="text-[#168B43]">
                      Possibilities.
                    </span>
                  </>
                }
                description="Dubai brings together modern landmarks, beautiful beaches, lively neighbourhoods, and memorable experiences. Discover a hotel location that works for your visit."
              />

              <div className="mt-7 space-y-4">
                {[
                  {
                    icon: Building2,
                    title: "City Hotels",
                    text: "Explore accommodation near shopping, attractions, and business districts.",
                  },
                  {
                    icon: MapPin,
                    title: "Popular Neighbourhoods",
                    text: "Discover stays across different areas of Dubai.",
                  },
                  {
                    icon: Heart,
                    title: "Relaxing Getaways",
                    text: "Find options for leisure trips, beach holidays, and family breaks.",
                  },
                ].map(({ icon: Icon, title, text }) => (
                  <div
                    key={title}
                    className="flex items-start gap-4 rounded-xl border border-[#DCEAE1] bg-white p-4"
                  >
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#E8F7EE] text-[#168B43]">
                      <Icon size={21} />
                    </div>

                    <div>
                      <h3 className="text-sm font-extrabold text-[#17324D]">
                        {title}
                      </h3>

                      <p className="mt-1 text-[12px] leading-6 text-[#718096]">
                        {text}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <Link
                to="/hotels"
                className="mt-8 inline-flex items-center gap-3 rounded-xl bg-[#168B43] px-7 py-4 text-sm font-extrabold text-white transition hover:bg-[#087D37]"
              >
                Explore Dubai Hotels
                <ArrowRight size={17} />
              </Link>
            </div>

            <div className="relative">
              <div className="overflow-hidden rounded-[24px]">
                <img
                  src={images.dubai}
                  alt="Dubai city skyline"
                  loading="lazy"
                  className="h-[390px] w-full object-cover sm:h-[570px]"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-[#064E3B]/50 to-transparent" />
              </div>

              <div className="absolute bottom-6 left-6 right-6 rounded-2xl border border-white/20 bg-white/95 p-5 shadow-xl backdrop-blur-md sm:left-8 sm:right-auto sm:min-w-[270px]">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#E8F7EE] text-[#168B43]">
                    <MapPin size={24} />
                  </div>

                  <div>
                    <p className="text-sm font-extrabold text-[#17324D]">
                      Explore Dubai
                    </p>

                    <p className="mt-1 text-xs text-[#718096]">
                      Find the right location
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* =====================================================
            HOW IT WORKS
        ===================================================== */}

        <section className="bg-white py-20 sm:py-24">
          <div className="mx-auto w-full max-w-[1536px] px-4 sm:px-6 lg:px-8">
            <SectionHeading
              label="Simple Hotel Discovery"
              title={
                <>
                  Your Stay in{" "}
                  <span className="text-[#168B43]">
                    Three Easy Steps.
                  </span>
                </>
              }
              description="A simple approach to exploring hotel options and planning your accommodation in Dubai."
              center
            />

            <div className="relative mt-14 grid gap-6 md:grid-cols-3">
              {steps.map((step, index) => {
                const Icon = step.icon;

                return (
                  <article
                    key={step.number}
                    className="relative rounded-[20px] border border-[#E3ECE6] bg-[#F8FBF9] p-7 sm:p-9"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-[#E8F7EE] text-[#168B43]">
                        <Icon size={25} />
                      </div>

                      <span className="text-[45px] font-extrabold leading-none text-[#DCECE2]">
                        {step.number}
                      </span>
                    </div>

                    <h3 className="mt-8 text-[21px] font-extrabold text-[#17324D]">
                      {step.title}
                    </h3>

                    <p className="mt-4 text-[13px] leading-7 text-[#718096]">
                      {step.description}
                    </p>

                    <div className="mt-7 flex items-center gap-2 text-[12px] font-extrabold text-[#168B43]">
                      Step {index + 1}
                      <ArrowRight size={15} />
                    </div>
                  </article>
                );
              })}
            </div>

            <div className="mt-11 text-center">
              <Link
                to="/hotels"
                className="inline-flex items-center gap-3 rounded-xl bg-[#168B43] px-8 py-4 text-sm font-extrabold text-white transition hover:bg-[#087D37]"
              >
                Find Your Hotel
                <ArrowRight size={17} />
              </Link>
            </div>
          </div>
        </section>

        {/* =====================================================
            FINAL CTA BANNER
        ===================================================== */}

        <section className="bg-white pb-20 sm:pb-24">
          <div className="mx-auto w-full max-w-[1536px] px-4 sm:px-6 lg:px-8">
            <div className="relative isolate overflow-hidden rounded-[24px] bg-[#064E3B] px-6 py-16 text-center sm:px-12 sm:py-20">
              <img
                src={images.pool}
                alt=""
                loading="lazy"
                className="absolute inset-0 -z-20 h-full w-full object-cover"
              />

              <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[#032F28]/95 via-[#064E3B]/90 to-[#064E3B]/80" />

              <div className="mx-auto max-w-[740px]">
                <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full border border-white/25 bg-white/10 text-[#B6F2CB]">
                  <Hotel size={26} />
                </div>

                <p className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#B6F2CB]">
                  Start Your Dubai Journey
                </p>

                <h2 className="mt-4 text-[34px] font-extrabold leading-tight tracking-[-0.04em] !text-white sm:text-[50px]">
                  Your Next Dubai Stay
                  <br />
                  Starts Here.
                </h2>

                <p className="mx-auto mt-5 max-w-[550px] text-sm leading-8 text-white/75">
                  Discover Dubai accommodation, explore hotel
                  options, and find a place that fits your
                  travel plans.
                </p>

                <div className="mt-8 flex flex-wrap justify-center gap-3">
                  <Link
                    to="/hotels"
                    className="inline-flex items-center gap-3 rounded-xl bg-[#168B43] px-7 py-4 text-sm font-extrabold text-white transition hover:bg-[#087D37]"
                  >
                    Explore Hotels
                    <ArrowRight size={17} />
                  </Link>

                  <Link
                    to="/services"
                    className="inline-flex items-center gap-3 rounded-xl border border-white/30 bg-white/10 px-7 py-4 text-sm font-extrabold text-white backdrop-blur-md transition hover:bg-white/20"
                  >
                    Our Services
                    <ArrowUpRight size={17} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>


      </main>
    </>
  );
}
